"use client";

import React, { useState, useEffect } from "react";

const VideoCard = ({ thumbnail, videoSrc, title }) => {
    const [open, setOpen] = useState(false);

    // Lock scroll when modal is open
    useEffect(() => {
        if (typeof window === 'undefined') return;
        document.body.style.overflow = open ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    const isYouTube = (u) => /(?:youtube\.com|youtu\.be)/i.test(u);

    const getYouTubeId = (u) => {
        if (!u) return null;
        try {
            const url = new URL(u);
            const host = url.hostname.replace(/^www\./i, "");
            if (host === "youtu.be") return url.pathname.split('/').filter(Boolean)[0] || null;
            if (host.endsWith("youtube.com")) {
                const v = url.searchParams.get('v');
                if (v) return v;
                const parts = url.pathname.split('/').filter(Boolean);
                if (parts[0] === 'embed' || parts[0] === 'shorts') return parts[1] || null;
            }
            const m = u.match(/(?:v=|\/embed\/|\.be\/|\/shorts\/)([^&?\/]+)/i);
            return m ? m[1] : null;
        } catch (e) {
            const m = u.match(/(?:v=|\/embed\/|\.be\/|\/shorts\/)([^&?\/]+)/i);
            return m ? m[1] : null;
        }
    };

    const youtubeId = isYouTube(videoSrc) ? getYouTubeId(videoSrc) : null;
    const youtubeThumb = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : null;
    const finalThumb = thumbnail || youtubeThumb;

    return (
        <>
            <div className="w-full sm:mt-4 md:mt-16">
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="group relative block w-full overflow-hidden focus:outline-none"
                >
                    <img
                        src={finalThumb}
                        alt={title || "Video"}
                        className="w-full h-[260px] sm:h-[320px] md:h-[550px] object-cover"
                    />

                    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/25 transition-colors duration-200" />

                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="relative flex items-center justify-center">
                            <span className="absolute h-16 w-16 rounded-full bg-[#003366] border border-white border-4" />
                            <span className="relative flex h-12 w-12 items-center justify-center rounded-full shadow-lg group-hover:scale-105 transition-transform duration-200" style={{ color: "white" }}>
                                <svg
                                    className="h-6 w-6"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                >
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            </span>
                        </div>
                    </div>
                </button>
            </div>

            {open && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 px-4"
                    onClick={() => setOpen(false)}
                    role="dialog"
                    aria-modal="true"
                >
                    <div
                        className="relative w-full max-w-4xl mx-auto bg-black rounded overflow-hidden max-h-[90vh]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button (inside modal, visible above header) */}
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label="Close video"
                            className="absolute top-3 right-3 text-white text-2xl z-50 bg-black/40 rounded-full w-8 h-8 flex items-center justify-center hover:bg-black/60"
                        >
                            ×
                        </button>

                        <div className="relative w-full pt-[56.25%]">
                                {isYouTube(videoSrc) ? (() => {
                                const id = youtubeId;
                                if (!id) {
                                    console.debug('VideoCard: no youtube id for', videoSrc);
                                    return (
                                        <div className="absolute inset-0 flex items-center justify-center text-white">
                                            <p>Invalid YouTube URL</p>
                                        </div>
                                    );
                                }

                                // Construct embed URL with controlled params to avoid malformed query strings
                                const embedBase = `https://www.youtube.com/embed/${id}`;
                                const params = new URLSearchParams({ autoplay: '1', rel: '0', modestbranding: '1' });
                                if (typeof window !== 'undefined' && window.location?.origin) {
                                    params.set('origin', window.location.origin);
                                }
                                const embedUrl = `${embedBase}?${params.toString()}`;
                                console.debug('VideoCard: embedUrl=', embedUrl, 'youtubeId=', id, 'videoSrc=', videoSrc);

                                return (
                                    <div className="absolute inset-0 h-full w-full">
                                        <iframe
                                            className="absolute inset-0 h-full w-full"
                                            src={embedUrl}
                                            title={title || 'Video'}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; autoplay"
                                            allowFullScreen
                                        />

                                        {/* Fallback link in case embedding is blocked */}
                                        <div className="absolute top-3 right-3 z-30">
                                            <a
                                                href={`https://www.youtube.com/watch?v=${id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-block bg-white/90 text-black px-3 py-1 rounded shadow"
                                            >
                                                Open on YouTube
                                            </a>
                                        </div>
                                    </div>
                                );
                            })() : (
                                <video
                                    src={videoSrc}
                                    controls
                                    autoPlay
                                    preload="metadata"
                                    className="absolute inset-0 h-full w-full object-cover"
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default VideoCard;
